import { DEFAULT_TERMS, parseTerms, termsFor } from '@bre-ticketing/domain';
import type { Terms, TermsSetting } from '@bre-ticketing/domain';

/**
 * What this deployment calls what it sells, for staff screens (D-378).
 *
 * The model's word is "performance"; staff read the deployment's date term
 * instead — "Games" for the Bees, "Dates" for a generic venue — from Admin →
 * Branding → Terminology. One setting per deployment that changes about never,
 * so it is read once before the first render (main.tsx), kept in
 * localStorage for the next visit, and read synchronously anywhere: a label,
 * a hint, a `confirm()` text. Until it arrives, the generic defaults.
 */

const STORAGE_KEY = 'bre.terms';

function readStored(): TermsSetting {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseTerms(JSON.parse(raw)) : DEFAULT_TERMS;
  } catch {
    return DEFAULT_TERMS;
  }
}

let setting: TermsSetting = readStored();

/** The staff noun for one dated occurrence: "game" / "games", or "date" / "dates". */
export const dateWords = (): Terms => termsFor(setting, { kind: 'date' });

/** The customer's noun: "game" for an event with an opponent, the event term otherwise. */
export const eventWords = (opponent?: boolean | null): Terms => termsFor(setting, { opponent });

/** The current setting, e.g. for a form that edits it. */
export const currentTerms = (): TermsSetting => setting;

/** Replace the words at once (after Branding is saved) and remember them. */
export function rememberTerms(next: unknown): void {
  setting = parseTerms(next);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(setting));
  } catch {
    // Private mode: the words still hold for this visit.
  }
}

/**
 * Reads the words from the public config. Never throws and never waits long:
 * a slow or failed read leaves the last known words (or the defaults).
 */
export async function loadTerms(timeoutMs = 1500): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch('/api/public/config', { credentials: 'same-origin', signal: controller.signal, headers: { accept: 'application/json' } });
    if (!response.ok) return;
    const config = (await response.json()) as { branding?: { terms?: unknown } };
    if (config.branding) rememberTerms(config.branding.terms);
  } catch {
    // Offline or slow: keep what we had.
  } finally {
    clearTimeout(timer);
  }
}
